/**
 * Browser error capture (tdd-0110 component 4, Layer 1).
 *
 * Every app that mounts `<ObservabilityInit>` gets a `browser.error` span for:
 *
 * - uncaught exceptions (`error.source = window`);
 * - unhandled promise rejections (`promise`);
 * - failed `<script>` / `<link>` / `<img>` loads, seen through a capture-phase
 *   listener because resource `error` events do not bubble (`resource`) — this
 *   is the "dynamic chunk 404 after a deploy" failure static apps actually hit;
 * - errors handed over by the React boundaries in `./browser/next` (`react`).
 *
 * ## Why this file has no runtime OTel import
 *
 * The listeners are installed **synchronously** when `<ObservabilityInit>`'s
 * module evaluates, long before the lazily-loaded SDK in `./browser.ts` has
 * arrived. Errors in that window (a hydration crash, a chunk that 404s) are the
 * ones most worth having, so they are captured into a bounded buffer and
 * flushed — with their original timestamps — once the SDK calls
 * `attachBrowserErrorEmitter`. Everything here is plain DOM and string work so
 * the eager part stays a few hundred bytes; the OTel API object is passed in.
 *
 * ## What reaches `window` under Next 16 (verified 2026-10-12, Next 16.3.5,
 * React 19.2.3, `next build && next start` and `output: "export"`)
 *
 * - **No `error.tsx` / `global-error.tsx`:** client render and effect errors DO
 *   reach `window` `error` (React's `reportError` for uncaught errors), and the
 *   page shows Next's bare "This page couldn't load". A server-component error
 *   reached on client navigation arrives as a minified "React error #441" with
 *   no digest. A server error on a hard load serves Next's own error document,
 *   so neither the layout nor `ObservabilityInit` ever runs: invisible.
 * - **With the boundaries:** the same errors are *caught* and do NOT reach
 *   `window` at all. The boundary is then the only client-side path, which is
 *   why `GlobalError` / `RouteError` record explicitly — and why there is no
 *   double count to dedupe in the normal case. (Same-object dedupe below covers
 *   the odd path that reports one error twice.)
 */
import { SAMPLING_PRIORITY_ATTR } from "./sampling-priority.js";
export { SAMPLING_PRIORITY_ATTR };
export const BROWSER_ERROR_SPAN_NAME = "browser.error";
export const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 4000;
const MAX_COMPONENT_STACK_LENGTH = 2000;
/** Same fingerprint at most once per window. */
export const FINGERPRINT_WINDOW_MS = 30_000;
/** Hard cap per page load, so a render loop cannot become a span flood. */
export const MAX_ERRORS_PER_PAGE = 50;
/** Errors held before the SDK arrives. Bounded; the oldest are kept. */
export const MAX_BUFFERED = 20;
/**
 * State lives on `globalThis`, not in module scope: if a bundle ends up with
 * two copies of this module (two chunk graphs, a version skew) they must share
 * one set of listeners and one rate limiter, or every error is sent twice.
 */
const STATE_KEY = Symbol.for("@workspace/observability/browser-errors");
function state() {
    const g = globalThis;
    let s = g[STATE_KEY];
    if (!s) {
        s = {
            installed: false,
            emit: null,
            buffer: [],
            lastEmitted: new Map(),
            count: 0,
            seen: new WeakSet(),
            onError: null,
            onRejection: null,
            now: () => Date.now(),
        };
        g[STATE_KEY] = s;
    }
    return s;
}
// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------
function truncate(value, max) {
    return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}
/** Strip query and hash: they can carry tokens and user input (PII). */
function stripQuery(url) {
    if (url.startsWith("data:"))
        return "data:…";
    if (url.startsWith("blob:"))
        return "blob:…";
    return url.replace(/[?#].*$/, "");
}
function currentPath() {
    try {
        return window.location.pathname;
    }
    catch {
        return "";
    }
}
/**
 * The first stack line that is a frame, with query strings removed so a
 * cache-busting `?dpl=` does not split one bug into many fingerprints.
 * Handles V8 (`    at fn (url:1:2)`) and Firefox/Safari (`fn@url:1:2`).
 */
export function topFrame(stack) {
    if (!stack)
        return "";
    for (const raw of stack.split("\n")) {
        const line = raw.trim();
        if (!line)
            continue;
        if (line.startsWith("at ") || /@\S+:\d+/.test(line) || /^\S+:\d+:\d+$/.test(line)) {
            return line.replace(/\?[^:)\s]*/g, "");
        }
    }
    return "";
}
/**
 * cyrb53 — a fast, well-distributed 53-bit string hash. Not cryptographic; it
 * only has to be stable across page loads and browsers, and rarely collide
 * among one app's distinct errors.
 */
function cyrb53(input) {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < input.length; i++) {
        const ch = input.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, "0");
}
/** Stable hash of type + message + top stack frame (tdd-0110 contract). */
export function fingerprint(type, message, stack) {
    return cyrb53(`${type}\n${message}\n${topFrame(stack)}`);
}
function describe(value) {
    if (value instanceof Error) {
        return { type: value.name || "Error", message: value.message ?? "", stack: value.stack };
    }
    // DOMException is an Error in modern engines, but not in every test runtime.
    if (value && typeof value === "object" && "message" in value) {
        const v = value;
        return {
            type: typeof v.name === "string" && v.name ? v.name : "Error",
            message: String(v.message ?? ""),
            stack: typeof v.stack === "string" ? v.stack : undefined,
        };
    }
    if (typeof value === "string")
        return { type: "Error", message: value };
    let message;
    try {
        message = JSON.stringify(value) ?? String(value);
    }
    catch {
        message = String(value);
    }
    return { type: value === null ? "null" : typeof value, message };
}
// ---------------------------------------------------------------------------
// Ignore list
// ---------------------------------------------------------------------------
const EXTENSION_SCHEMES = ["chrome-extension://", "moz-extension://", "safari-web-extension://", "safari-extension://"];
function isExtensionUrl(value) {
    return !!value && EXTENSION_SCHEMES.some((s) => value.includes(s));
}
/**
 * Noise that would otherwise dominate the error rate:
 *
 * - `ResizeObserver loop …` — a benign spec notification, not a bug.
 * - `Script error.` with no error object — a cross-origin script's opaque
 *   report; there is nothing in it to act on.
 * - Errors whose origin is a browser extension (filename or top frame) — not
 *   our code, and not fixable by us.
 */
export function shouldIgnore(message, stack, filename, hasError = true) {
    if (message.startsWith("ResizeObserver loop"))
        return true;
    if (!hasError && /^Script error\.?$/.test(message.trim()))
        return true;
    if (isExtensionUrl(filename))
        return true;
    if (isExtensionUrl(topFrame(stack)))
        return true;
    return false;
}
// ---------------------------------------------------------------------------
// Capture → rate limit → emit or buffer
// ---------------------------------------------------------------------------
/**
 * Decide whether to record, then emit (SDK live) or buffer (SDK still loading).
 * Rate limiting is applied here, at capture, so buffered errors count against
 * the per-page cap and a flood before load cannot overflow it on flush.
 */
function capture(raw, original) {
    const s = state();
    if (original && typeof original === "object") {
        if (s.seen.has(original))
            return;
        s.seen.add(original);
    }
    const message = truncate(raw.message, MAX_MESSAGE_LENGTH);
    const fp = fingerprint(raw.type, raw.message, raw.stack);
    const now = s.now();
    if (s.count >= MAX_ERRORS_PER_PAGE)
        return;
    const last = s.lastEmitted.get(fp);
    if (last !== undefined && now - last < FINGERPRINT_WINDOW_MS)
        return;
    s.lastEmitted.set(fp, now);
    s.count++;
    const captured = {
        ...raw,
        message,
        stack: raw.stack ? truncate(raw.stack, MAX_STACK_LENGTH) : undefined,
        componentStack: raw.componentStack
            ? truncate(raw.componentStack, MAX_COMPONENT_STACK_LENGTH)
            : undefined,
        fingerprint: fp,
        urlPath: currentPath(),
        time: now,
    };
    if (s.emit) {
        safeEmit(s.emit, captured);
    }
    else if (s.buffer.length < MAX_BUFFERED) {
        s.buffer.push(captured);
    }
}
function safeEmit(emit, captured) {
    try {
        emit(captured);
    }
    catch {
        // Reporting an error must never become a second error.
    }
}
/**
 * Record an error as a `browser.error` span, through the same rate limiter,
 * fingerprint and ignore list as the global handlers. Safe to call before the
 * SDK has loaded (buffered), during SSR (no-op), and with any thrown value.
 *
 * @example
 * ```ts
 * try { await save(); } catch (err) { recordBrowserError(err, { source: "react" }); throw err; }
 * ```
 */
export function recordBrowserError(error, options) {
    if (typeof window === "undefined")
        return;
    try {
        const d = describe(error);
        if (shouldIgnore(d.message, d.stack))
            return;
        capture({
            ...d,
            source: options.source,
            componentStack: options.componentStack ?? undefined,
            digest: options.digest ?? undefined,
            boundary: options.boundary,
        }, error);
    }
    catch {
        // never throws
    }
}
const RESOURCE_TAGS = new Set(["SCRIPT", "LINK", "IMG"]);
function handleErrorEvent(event) {
    const target = event.target;
    // Resource-load failure: dispatched at the element, seen here only because
    // the listener is in the capture phase (these events do not bubble).
    if (target && target !== window && typeof target.tagName === "string") {
        if (!RESOURCE_TAGS.has(target.tagName))
            return;
        const url = stripQuery(target.src || target.href || "");
        if (isExtensionUrl(url))
            return;
        const tag = target.tagName.toLowerCase();
        capture({
            type: "ResourceLoadError",
            message: `Failed to load ${tag}: ${url || "(no url)"}`,
            source: "resource",
        });
        return;
    }
    const e = event;
    // No error object = an opaque cross-origin report or a bare `message`.
    const hasError = e.error !== undefined && e.error !== null;
    const d = hasError ? describe(e.error) : { type: "Error", message: e.message ?? "" };
    const stack = "stack" in d ? d.stack : undefined;
    if (shouldIgnore(d.message, stack, e.filename, hasError))
        return;
    capture({ ...d, source: "window" }, e.error);
}
function handleRejection(event) {
    const d = describe(event.reason);
    if (shouldIgnore(d.message, d.stack))
        return;
    // `reject("text")` has no type of its own; "Error" would read as a real one.
    const type = d.type === "Error" && !(event.reason instanceof Error) ? "UnhandledRejection" : d.type;
    capture({ ...d, type, source: "promise" }, event.reason);
}
/**
 * Install the global listeners. Idempotent, synchronous, and cheap — called at
 * module evaluation of `<ObservabilityInit>` so errors before the SDK loads
 * are not lost, and again by `initBrowserTracing` for apps that call
 * `register()` directly.
 */
export function installBrowserErrorCapture() {
    if (typeof window === "undefined")
        return;
    const s = state();
    if (s.installed)
        return;
    s.installed = true;
    s.onError = (event) => {
        try {
            handleErrorEvent(event);
        }
        catch {
            // never throws
        }
    };
    s.onRejection = (event) => {
        try {
            handleRejection(event);
        }
        catch {
            // never throws
        }
    };
    window.addEventListener("error", s.onError, true);
    window.addEventListener("unhandledrejection", s.onRejection);
}
// ---------------------------------------------------------------------------
// Emission (called by the SDK once loaded)
// ---------------------------------------------------------------------------
/** Build the span for one captured error, per the tdd-0110 contract. */
export function emitBrowserErrorSpan(api, tracer, c) {
    const attributes = {
        "exception.type": c.type,
        "exception.message": c.message,
        "error.source": c.source,
        "error.fingerprint": c.fingerprint,
        "url.path": c.urlPath,
        // A start attribute, so the priority sampler sees it: errors bypass the
        // prod 0.2 ratio. Without this, 80% of error root spans are dropped.
        [SAMPLING_PRIORITY_ATTR]: 1,
    };
    if (c.stack)
        attributes["exception.stacktrace"] = c.stack;
    if (c.componentStack)
        attributes["react.component_stack"] = c.componentStack;
    if (c.digest)
        attributes["next.digest"] = c.digest;
    if (c.boundary)
        attributes["react.boundary"] = c.boundary;
    const options = { kind: api.SpanKind.INTERNAL, attributes, startTime: c.time };
    // Join the current trace only when that trace is being kept: a child of an
    // unsampled parent is dropped by ParentBased regardless of priority. Else
    // start a root (which the priority sampler keeps) and link the parent.
    const active = api.trace.getActiveSpan()?.spanContext();
    const activeSampled = !!active && (active.traceFlags & api.TraceFlags.SAMPLED) !== 0;
    if (!activeSampled) {
        options.root = true;
        if (active && active.traceId && !/^0+$/.test(active.traceId)) {
            options.links = [{ context: active }];
        }
    }
    const span = tracer.startSpan(BROWSER_ERROR_SPAN_NAME, options);
    span.recordException({ name: c.type, message: c.message, stack: c.stack }, c.time);
    span.setStatus({ code: api.SpanStatusCode.ERROR, message: c.message });
    span.end(c.time);
}
/**
 * Hand the capture layer a live tracer, and flush whatever was buffered
 * before the SDK arrived. Called once from `initBrowserTracing`.
 */
export function attachBrowserErrorEmitter(api, serviceName) {
    const s = state();
    const tracer = api.trace.getTracer(`${serviceName}/errors`);
    s.emit = (c) => emitBrowserErrorSpan(api, tracer, c);
    const pending = s.buffer.splice(0);
    for (const c of pending)
        safeEmit(s.emit, c);
}
/** Test-only: drop listeners and all state so each test starts clean. */
export function __resetBrowserErrorCaptureForTests(now) {
    const s = state();
    if (typeof window !== "undefined") {
        if (s.onError)
            window.removeEventListener("error", s.onError, true);
        if (s.onRejection)
            window.removeEventListener("unhandledrejection", s.onRejection);
    }
    s.installed = false;
    s.emit = null;
    s.buffer = [];
    s.lastEmitted = new Map();
    s.count = 0;
    s.seen = new WeakSet();
    s.onError = null;
    s.onRejection = null;
    s.now = now ?? (() => Date.now());
}
